import type { ReactNode } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CalloutPin, type TabCalloutProps } from "./CalloutPin";
import {
  CLICKS_COLOR,
  IMPRESSIONS_COLOR,
  PERF_CALLOUTS,
  PERF_WEEKS,
  QUERY_ROWS,
  SURFACE,
} from "./gscData";
import {
  formatNumber,
  GRID_STROKE,
  TICK_STYLE,
  TOOLTIP_CONTENT_STYLE,
  TOOLTIP_ITEM_STYLE,
  TOOLTIP_LABEL_STYLE,
} from "./chartTheme";

/* ------------------------------------------------------------------ */

interface StatTileProps {
  label: string;
  value: string;
  accent?: string;
  children?: ReactNode;
}

/** The number IS the chart — big proportional figures in Inter, label above. */
function StatTile({ label, value, accent, children }: StatTileProps) {
  return (
    <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4">
      {accent && (
        <span
          className="absolute inset-x-0 top-0 h-1 rounded-t-2xl"
          style={{ background: accent }}
        />
      )}
      <div className="flex items-center gap-2.5">
        {accent && <span className="h-3 w-3 rounded-full" style={{ background: accent }} />}
        <p className="text-xl text-white/60">{label}</p>
      </div>
      <p className="mt-1 font-sans text-5xl font-semibold text-white">{value}</p>
      {children}
    </div>
  );
}

function SeriesChip({ color, name }: { color: string; name: string }) {
  return (
    <span className="flex items-center gap-2.5 pl-1">
      <span className="h-3 w-3 rounded-full" style={{ background: color }} />
      <span className="text-xl font-medium text-white/80">{name}</span>
    </span>
  );
}

function ChartTooltip() {
  return (
    <Tooltip
      contentStyle={TOOLTIP_CONTENT_STYLE}
      labelStyle={TOOLTIP_LABEL_STYLE}
      itemStyle={TOOLTIP_ITEM_STYLE}
      cursor={{ stroke: "rgba(255,255,255,0.2)", strokeWidth: 1 }}
      formatter={(value) => formatNumber(value)}
    />
  );
}

function HeaderDot({ color }: { color: string }) {
  return <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: color }} />;
}

/* ------------------------------------------------------------------ */

export function PerformanceTab({ openId, visited, onOpen }: TabCalloutProps) {
  const pinProps = { openId, visited, onOpen };
  return (
    <div className="flex h-full flex-col gap-4">
      {/* Stat tiles */}
      <div className="grid shrink-0 grid-cols-4 gap-4">
        <StatTile label="Total clicks" value="6.4K" accent={CLICKS_COLOR} />
        <StatTile label="Total impressions" value="408K" accent={IMPRESSIONS_COLOR} />
        <StatTile label="Average CTR" value="1.6%" />
        <StatTile label="Average position" value="13.4" />
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-[1fr_1.15fr] gap-4">
        {/* Two vertically stacked synced panels — one y-scale each, never dual-axis */}
        <div className="relative flex min-h-0 flex-col gap-2 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
          <div className="flex min-h-0 flex-col" style={{ flex: "55 1 0%" }}>
            <SeriesChip color={CLICKS_COLOR} name="Clicks" />
            <div className="min-h-0 flex-1">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={PERF_WEEKS}
                  syncId="gsc-perf"
                  margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
                >
                  <CartesianGrid stroke={GRID_STROKE} vertical={false} />
                  <XAxis dataKey="week" hide />
                  <YAxis
                    width={54}
                    domain={[0, 500]}
                    ticks={[0, 250, 500]}
                    tick={TICK_STYLE}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v: number) => (v >= 1000 ? `${v / 1000}K` : `${v}`)}
                  />
                  <ChartTooltip />
                  <Line
                    type="monotone"
                    dataKey="clicks"
                    name="Clicks"
                    stroke={CLICKS_COLOR}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 5, stroke: SURFACE, strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="flex min-h-0 flex-col" style={{ flex: "45 1 0%" }}>
            <SeriesChip color={IMPRESSIONS_COLOR} name="Impressions" />
            <div className="min-h-0 flex-1">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={PERF_WEEKS}
                  syncId="gsc-perf"
                  margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
                >
                  <CartesianGrid stroke={GRID_STROKE} vertical={false} />
                  <XAxis
                    dataKey="week"
                    tick={TICK_STYLE}
                    tickLine={false}
                    axisLine={false}
                    interval={2}
                    tickMargin={8}
                    height={30}
                  />
                  <YAxis
                    width={54}
                    domain={[0, 30000]}
                    ticks={[0, 15000, 30000]}
                    tick={TICK_STYLE}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v: number) => (v >= 1000 ? `${v / 1000}K` : `${v}`)}
                  />
                  <ChartTooltip />
                  <Line
                    type="monotone"
                    dataKey="impressions"
                    name="Impressions"
                    stroke={IMPRESSIONS_COLOR}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 5, stroke: SURFACE, strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ③ Date range + compare — pinned on the time axis area */}
          <CalloutPin
            callout={PERF_CALLOUTS.compare}
            className="absolute left-[56%] top-[10%]"
            {...pinProps}
          />
        </div>

        {/* Queries table */}
        <div className="min-h-0 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
          <table className="w-full">
            <thead>
              <tr className="text-xl uppercase tracking-wider text-white/50">
                <th className="relative px-4 py-2.5 pr-12 text-left font-medium">
                  Query
                  {/* ② pages earning unexpected queries — anchored on the
                      dimension header, where the Queries|Pages switch lives */}
                  <CalloutPin
                    callout={PERF_CALLOUTS.pages}
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                    {...pinProps}
                  />
                </th>
                <th className="px-3 py-2.5 text-right font-medium">
                  <span className="inline-flex items-center gap-2">
                    <HeaderDot color={CLICKS_COLOR} />
                    Clicks
                  </span>
                </th>
                <th className="px-3 py-2.5 text-right font-medium">
                  <span className="inline-flex items-center gap-2">
                    <HeaderDot color={IMPRESSIONS_COLOR} />
                    Impr.
                  </span>
                </th>
                <th className="px-3 py-2.5 text-right font-medium">CTR</th>
                <th className="px-4 py-2.5 text-right font-medium">Pos.</th>
              </tr>
            </thead>
            <tbody>
              {QUERY_ROWS.map((r) => {
                const gapPin = r.query === "best running shoes for flat feet";
                return (
                  <tr
                    key={r.query}
                    className={`border-t border-white/[0.06] transition-colors hover:bg-white/5 ${
                      r.gap ? "bg-accent-amber/[0.06]" : ""
                    }`}
                  >
                    <td
                      className={`relative px-4 py-2.5 text-xl text-white/90 ${
                        gapPin ? "pr-12" : ""
                      }`}
                    >
                      {r.query}
                      {/* ① impressions but weak position = content gap */}
                      {gapPin && (
                        <CalloutPin
                          callout={PERF_CALLOUTS.gaps}
                          className="absolute right-0 top-1/2 -translate-y-1/2"
                          {...pinProps}
                        />
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right text-xl tabular-nums text-white/85">
                      {r.clicks.toLocaleString("en-US")}
                    </td>
                    <td className="px-3 py-2.5 text-right text-xl tabular-nums text-white/85">
                      {r.impressions.toLocaleString("en-US")}
                    </td>
                    <td className="px-3 py-2.5 text-right text-xl tabular-nums text-white/85">
                      {r.ctr}
                    </td>
                    <td
                      className={`px-4 py-2.5 text-right text-xl tabular-nums ${
                        r.gap ? "font-semibold text-accent-amber" : "text-white/85"
                      }`}
                    >
                      {r.position.toFixed(1)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
